import { PointCloudObject } from "@/object-cache";
import { GUID } from "@faro-lotv/foundation";
import { LodTreeNode } from "@faro-lotv/lotv";

export type NodeStats = {
  /** The number of nodes. */
  nodes: number;
  /** The total number of points in these nodes. */
  points?: number;
};

export type PointCloudStats = {
  /** Stats about the nodes actively being rendered. */
  gpu: NodeStats;
  /** Stats about the nodes determined to be visible. */
  visible: NodeStats;
  /** Stats about the nodes cached in memory. */
  memory: NodeStats;
  /** The rendered depth in the point cloud tree. */
  depth: NodeStats;
  /** Stats about the nodes currently downloading. */
  loading: NodeStats;
  /** Stats about the nodes waiting for download. */
  queued: NodeStats;
};

export type PointCloudStatsMap = Record<GUID, PointCloudStats>;

/**
 * @param pointCloudObjects The point clouds to calculate stats for.
 * @returns Stats about the nodes and points in the point clouds.
 */
export function calculateNodeStats(
  pointCloudObjects: PointCloudObject[],
): PointCloudStatsMap {
  const map: PointCloudStatsMap = {};

  pointCloudObjects.forEach((pc) => {
    map[pc.iElement.id] = {
      gpu: statsForNodes(pc, pc.nodesInGPU.keys()),
      loading: { nodes: pc.lodTreeFetcher.downloadingNodesCount },
      queued: { nodes: pc.lodTreeFetcher.nodesWaitingForDownloadCount },
      visible: statsForNodes(
        pc,
        pc.getVisibleNodes().map((n) => n.id),
      ),
      memory: statsForNodes(pc, pc.nodesInMemory.keys()),
      depth: {
        nodes: maxRenderDepthRecursive(pc.tree.root, [...pc.nodesInGPU.keys()]),
      },
    };
  });

  return map;
}

function statsForNodes(
  pointCloudObject: PointCloudObject,
  nodeIds: Iterable<number>,
): NodeStats {
  let nodes = 0;
  let points = 0;

  for (const nodeId of nodeIds) {
    nodes += 1;
    const node = pointCloudObject.tree.getNode(nodeId);
    points += node.numPoints;
  }

  return {
    nodes,
    points,
  };
}

/**
 * @param node The node to start at.
 * @param renderedNodes A list of node indexes that are currently being rendered.
 * @param curDepth For recursive calls, the current depth in the tree.
 * @returns The maximum depth that a node is rendered at.
 *  - 0 means nothing is rendered in this sub-tree,
 *  - 1 means only the root is rendered.
 */
function maxRenderDepthRecursive(
  node: LodTreeNode,
  renderedNodes: number[],
  curDepth = 1,
): number {
  // Depth-first search
  const maxChildDepth = node.children.reduce((max, child) => {
    const childDepth = maxRenderDepthRecursive(
      child,
      renderedNodes,
      curDepth + 1,
    );
    return Math.max(max, childDepth);
  }, 0);

  if (maxChildDepth > 0) {
    // If any child is rendered, its depth will be greater than the current depth
    return maxChildDepth;
  }

  if (renderedNodes.includes(node.id)) {
    // If the current node is rendered, the tree is rendered at least to this depth
    return curDepth;
  }

  // Nothing rendered in this sub-tree
  return 0;
}
